import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows } from '@/lib/fetchAllRows';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, CurrencyDisplay, PercentDisplay } from '@/components/ui/stat-card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Legend, LineChart, Line, ComposedChart } from 'recharts';
import { format, parse, eachWeekOfInterval, eachDayOfInterval, startOfWeek, endOfWeek, differenceInDays } from 'date-fns';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';

const formatCompact = (amount: number) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return amount.toFixed(0);
};

const formatCurrencyValue = (amount: number) => {
    const absAmount = Math.abs(amount);
    return new Intl.NumberFormat('en-GH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(absAmount);
};

interface ContractRepaymentsAnalyticsProps {
    contractId?: number;
    monthRange: { from: Date; to: Date };
}

export default function ContractRepaymentsAnalytics({ contractId, monthRange }: ContractRepaymentsAnalyticsProps) {

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    // Fetch repayments data directly from the repayments table
    const { data: currentData, isLoading: currentLoading, error: currentError } = useQuery({
        queryKey: ['repayments-analytics', fromDate, toDate, contractId],
        queryFn: async () => {
            // Every repayment in the range. `fetchAllRows` pages in primary-key order, so the offset
            // pages neither repeat nor skip a row; it stops on the server's count, not on a short
            // page; and a failed page or a missing row throws instead of ending in a partial total.
            const allRepayments = await fetchAllRows({
                label: 'repayments summary',
                orderedBy: 'id',
                page: (from, to) => {
                    let query = supabase
                        .from('repayments')
                        .select('id, contract_id, transactions!inner(amount, transaction_at)', { count: 'exact' })
                        .gte('transactions.transaction_at', fromDate)
                        .lte('transactions.transaction_at', toDate + 'T23:59:59');

                    if (contractId) query = query.eq('contract_id', contractId);

                    return query.order('id', { ascending: true }).range(from, to);
                },
                keyOf: (r) => r.id,
            });

            // Calculate analytics from the fetched data
            let totalCollected = 0;
            const repaymentCount = allRepayments.length;
            const uniqueContractIds = new Set<number>();

            allRepayments.forEach(r => {
                const trans = r.transactions as { amount: number; transaction_at: string };
                totalCollected += trans.amount || 0;
                uniqueContractIds.add(r.contract_id);
            });

            const avgPayment = repaymentCount > 0 ? totalCollected / repaymentCount : 0;

            return {
                totalCollected,
                avgPayment,
                repaymentCount,
                uniqueContracts: uniqueContractIds.size,
                filteredRepayments: allRepayments,
            };
        },
    });

    // Query for expected repayments (contracts with instalments + loan instalments)
    const { data: expectedData } = useQuery({
        queryKey: ['expected-repayments-analytics', fromDate, toDate, contractId],
        queryFn: async () => {
            // Get contracts with their status history (match ContractRepaymentsOutstanding pattern)
            let contractsQuery = supabase
                .from('contracts')
                .select(`
                    id,
                    contract_updates(status, created_at)
                `);

            if (contractId) contractsQuery = contractsQuery.eq('id', contractId);

            const { data: contracts, error: contractsError } = await contractsQuery;

            if (contractsError || !contracts) {
                return { totalExpected: 0, weeklyExpected: {} as Record<string, number> };
            }

            // Filter to IN_PROGRESS contracts based on latest update status
            const activeContracts = contracts.filter(c => {
                const updates = (c.contract_updates as any[]) || [];
                if (updates.length === 0) return contractId ? true : false;
                const latest = updates.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                )[0];
                return contractId ? true : latest?.status === 'IN_PROGRESS';
            });
            const activeContractIds = activeContracts.map(c => c.id);

            if (activeContractIds.length === 0) return { totalExpected: 0, weeklyExpected: {} as Record<string, number> };

            // Get contract instalments (weekly instalment amount per contract)
            const { data: contractInstalments } = await supabase
                .from('contract_instalments')
                .select('contract_id, amount, starts_at')
                .in('contract_id', activeContractIds)
                .lte('starts_at', toDate)
                .order('starts_at', { ascending: false });

            // Get loan instalments in the period
            const { data: loanInstalments } = await supabase
                .from('loan_instalments')
                .select('amount, starts_at, loans!inner(contract_id)')
                .in('loans.contract_id', activeContractIds)
                .gte('starts_at', fromDate)
                .lte('starts_at', toDate + 'T23:59:59');

            const periodStart = monthRange.from;
            const periodEnd = monthRange.to;
            const weeks = eachWeekOfInterval({ start: periodStart, end: periodEnd }, { weekStartsOn: 1 });
            const weeksInPeriod = weeks.length;

            // Build maps for weekly amounts and start dates
            const weeklyInstalmentByContract = new Map<number, number>();
            const contractStartDates = new Map<number, string>();
            const seenContracts = new Set<number>();

            contractInstalments?.forEach(ci => {
                if (!seenContracts.has(ci.contract_id)) {
                    seenContracts.add(ci.contract_id);
                    weeklyInstalmentByContract.set(ci.contract_id, ci.amount || 0);
                    if (ci.starts_at) {
                        contractStartDates.set(ci.contract_id, ci.starts_at);
                    }
                }
            });

            // Calculate expected per contract and per week
            let totalExpected = 0;
            const weeklyExpected: Record<string, number> = {};
            weeks.forEach(w => weeklyExpected[format(w, 'yyyy-MM-dd')] = 0);

            activeContracts.forEach(c => {
                const weeklyAmount = weeklyInstalmentByContract.get(c.id) || 0;
                const startDate = contractStartDates.get(c.id);

                // Calculate weeks for this contract
                let contractWeeks = weeksInPeriod;
                if (startDate) {
                    // starts_at is a DATE column: parse it as a local calendar day so it compares on
                    // the same basis as monthRange (local Dates). new Date('yyyy-MM-dd') is a
                    // UTC-midnight instant, which reads as the previous day at negative UTC offsets
                    // and bills a week the contract had not started.
                    const startDateObj = parse(startDate.substring(0, 10), 'yyyy-MM-dd', new Date());
                    if (startDateObj > periodStart) {
                        const daysActive = differenceInDays(periodEnd, startDateObj) + 1;
                        contractWeeks = Math.max(0, Math.ceil(daysActive / 7));
                    }
                    if (startDateObj > periodEnd) {
                        contractWeeks = 0;
                    }
                }

                // Add to total
                totalExpected += weeklyAmount * contractWeeks;

                // Distribute across weeks
                const contractStart = startDate
                    ? parse(startDate.substring(0, 10), 'yyyy-MM-dd', new Date())
                    : null;
                weeks.forEach(weekDate => {
                    const weekKey = format(weekDate, 'yyyy-MM-dd');
                    const weekEnd = endOfWeek(weekDate, { weekStartsOn: 1 });
                    if (!contractStart || contractStart <= weekEnd) {
                        weeklyExpected[weekKey] = (weeklyExpected[weekKey] || 0) + weeklyAmount;
                    }
                });
            });

            // Add loan instalments
            loanInstalments?.forEach(li => {
                const loanContractId = (li.loans as any)?.contract_id;
                if (loanContractId && activeContractIds.includes(loanContractId)) {
                    const contractStart = contractStartDates.get(loanContractId);
                    if (!contractStart || li.starts_at >= contractStart) {
                        totalExpected += li.amount || 0;
                        // Same local-calendar basis as the weeklyExpected keys, which come from
                        // eachWeekOfInterval over monthRange. A UTC-midnight instant lands on the
                        // previous week at negative UTC offsets, so the key misses the map and the
                        // amount silently vanishes from the chart while totalExpected still counts it.
                        const weekKey = format(startOfWeek(parse(li.starts_at.substring(0, 10), 'yyyy-MM-dd', new Date()), { weekStartsOn: 1 }), 'yyyy-MM-dd');
                        if (weekKey in weeklyExpected) {
                            weeklyExpected[weekKey] = (weeklyExpected[weekKey] || 0) + (li.amount || 0);
                        }
                    }
                }
            });

            return { totalExpected, weeklyExpected };
        },
    });

    // Weekly chart data (includes expected for new charts)
    const weeklyChartData = useMemo(() => {
        if (!currentData) return [];

        // Generate all weeks in the range (Monday start)
        const weeks = eachWeekOfInterval({ start: monthRange.from, end: monthRange.to }, { weekStartsOn: 1 });
        const weeklyMap = new Map<string, { collected: number; count: number }>();

        weeks.forEach(w => {
            weeklyMap.set(format(w, 'yyyy-MM-dd'), { collected: 0, count: 0 });
        });

        currentData.filteredRepayments?.forEach(r => {
            const trans = r.transactions as { amount: number; transaction_at: string };
            // Bucket by the stored Ghana day (same idiom as the heatmap below), not the viewer's
            // local week: a local parse shifts late-evening payments into the next week's bar — or
            // drops them entirely when the shifted key falls outside the range.
            const txDay = parse(trans.transaction_at.substring(0, 10), 'yyyy-MM-dd', new Date());
            const weekKey = format(startOfWeek(txDay, { weekStartsOn: 1 }), 'yyyy-MM-dd');

            if (weeklyMap.has(weekKey)) {
                const entry = weeklyMap.get(weekKey)!;
                entry.collected += trans.amount || 0;
                entry.count += 1;
            }
        });

        return Array.from(weeklyMap.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([weekStart, data]) => {
                const expected = expectedData?.weeklyExpected?.[weekStart] || 0;
                const collectionRate = expected > 0 ? (data.collected / expected) * 100 : 0;
                return {
                    week: format(parse(weekStart, 'yyyy-MM-dd', new Date()), 'MMM d'),
                    collected: data.collected,
                    expected,
                    collectionRate: Math.min(collectionRate, 100), // Cap at 100%
                    count: data.count,
                };
            });
    }, [currentData, expectedData, monthRange]);

    // Daily payment data for calendar heatmap
    const dailyPaymentData = useMemo(() => {
        if (!currentData?.filteredRepayments) return [];

        const dailyMap = new Map<string, number>();

        // Initialize all days in range
        const days = eachDayOfInterval({ start: monthRange.from, end: monthRange.to });
        days.forEach(d => {
            dailyMap.set(format(d, 'yyyy-MM-dd'), 0);
        });

        // Count payments per day
        currentData.filteredRepayments.forEach(rep => {
            const trans = rep.transactions as { amount: number; transaction_at: string };
            const dateKey = trans.transaction_at.substring(0, 10);
            if (dailyMap.has(dateKey)) {
                dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + 1);
            }
        });

        /*
         * `date` MUST be a Date, not the 'yyyy-MM-dd' string. react-calendar-heatmap places a cell by
         * `floor((convertToDate(value.date) - startDateWithEmptyDays) / 86_400_000)`, and its origin is
         * built with `new Date(y, m, d)` — LOCAL midnight — from `startDate`/`endDate`, which are the
         * local-midnight `monthRange` bounds. `new Date('2026-08-10')` is UTC midnight by spec, so at a
         * negative UTC offset the numerator loses hours and every square lands one column early (the
         * tooltip, which reads the raw string, then contradicts the square it is attached to). Parsing
         * to a LOCAL date puts both sides of the subtraction on one basis, in every viewer zone.
         *
         * NOON, not midnight, and only because that division floors: a range spanning a DST
         * spring-forward makes local-midnight-to-local-midnight 24h-1h, and every day after the
         * transition falls a column early again — the exact bug this fix is here to remove, just
         * rarer. Noon leaves an 11–13h remainder that no one-hour shift can push across a day
         * boundary. The library never renders `value.date` (it is read once, at cjs.js:323, for this
         * index and nothing else), so the clock part is invisible; the tooltip reads `day` below.
         */
        return Array.from(dailyMap.entries()).map(([day, count]) => ({
            date: parse(`${day} 12:00`, 'yyyy-MM-dd HH:mm', new Date()),
            day,
            count,
        }));
    }, [currentData, monthRange]);

    // Calculate period days for heatmap width
    const periodDays = Math.ceil((monthRange.to.getTime() - monthRange.from.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (currentLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                </div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    // A failed or incomplete read must not render as "Total Collected 0" beside a full Expected.
    if (currentError) {
        return (
            <p className="py-8 text-center text-sm text-muted-foreground">
                Repayments could not be loaded: {(currentError as Error).message}
            </p>
        );
    }

    // Calculate derived stats
    const totalExpected = expectedData?.totalExpected || 0;
    const totalCollected = currentData?.totalCollected || 0;
    const netShortfall = Math.max(0, totalExpected - totalCollected);
    const collectionRate = totalExpected > 0 ? (totalCollected / totalExpected) * 100 : 0;

    return (
        <div className="space-y-6">
            {/* Stats Row */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Expected"
                    value={<CurrencyDisplay amount={totalExpected} />}
                    description="The total amount that should have been collected based on instalment schedules for all active contracts within the selected date range."
                />
                <StatCard
                    title="Total Collected"
                    value={<CurrencyDisplay amount={totalCollected} />}
                    description="The actual payments received and recorded in the system during the selected period, including partial and overpayments."
                />
                <StatCard
                    title="Net Shortfall"
                    value={<CurrencyDisplay amount={netShortfall} />}
                    description="The difference between total expected and total collected. A value of GH₵0 means all expected payments have been received or exceeded."
                />
                <StatCard
                    title="Collection Rate"
                    value={<PercentDisplay value={collectionRate} />}
                    description="The percentage of expected payments that have been collected. Above 100% indicates overpayments; 100% means fully on track."
                />
                <StatCard
                    title="Avg Transaction"
                    value={<CurrencyDisplay amount={currentData?.avgPayment || 0} />}
                    description="The average size of each repayment transaction in the period. Helps identify typical payment behavior and spot anomalies."
                />
            </div>

            {/* Charts Row - Side by Side */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* Weekly Collection Trend Chart (with Expected) */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Weekly Collection Trend</CardTitle>
                        <CardDescription>Expected vs collected repayments per week</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={weeklyChartData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="week" className="text-xs" />
                                <YAxis className="text-xs" tickFormatter={(v) => formatCompact(v)} />
                                <RechartsTooltip
                                    formatter={(value: number) => `${formatCurrencyValue(value)}GH₵`}
                                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                                />
                                <Legend />
                                <Area type="monotone" dataKey="expected" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.3} name="Expected" />
                                <Area type="monotone" dataKey="collected" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} name="Collected" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Payment Frequency Calendar Heatmap */}
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Payment Frequency</CardTitle>
                        <CardDescription>Daily payment activity (intensity = number of payments)</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto" style={{ width: '100%', maxWidth: `${Math.ceil(periodDays / 7) * 45 + 50}px` }}>
                            <CalendarHeatmap
                                startDate={monthRange.from}
                                endDate={monthRange.to}
                                values={dailyPaymentData}
                                classForValue={(value) => {
                                    if (!value || value.count === 0) return 'fill-muted';
                                    if (value.count >= 10) return 'fill-green-700';
                                    if (value.count >= 5) return 'fill-green-500';
                                    if (value.count >= 2) return 'fill-green-400';
                                    return 'fill-green-300';
                                }}
                                titleForValue={(value) => {
                                    if (!value || !value.day) return '';
                                    return `${value.day}: ${value.count} payment${value.count !== 1 ? 's' : ''}`;
                                }}
                                showWeekdayLabels
                                gutterSize={2}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-4 text-xs text-muted-foreground">
                            <span>Less</span>
                            <div className="w-3 h-3 rounded-sm bg-muted"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-300"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-400"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-500"></div>
                            <div className="w-3 h-3 rounded-sm bg-green-700"></div>
                            <span>More</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
